/**
 * Compute the time windows when interviewers are *collectively* available.
 *
 * - `one_on_one` → UNION: any interviewer's working hours count.
 *   Used when you just need one of the listed people — picking a slot
 *   that fits anyone is fine.
 *
 * - `panel` → INTERSECTION: every interviewer must be free at the
 *   same moment. Meeting rooms are not counted as "required humans".
 *
 * Worked example — panel with two people:
 *
 *   User A: Mon 09:00–11:00, 14:00–17:00
 *   User B: Mon 10:00–15:00, 18:00–20:00
 *
 *   Common (both free): 10:00–11:00, 14:00–15:00
 *
 * The intersection uses a sweep-line algorithm: build "slot starts"
 * and "slot ends" events, sort them, and walk left-to-right. When the
 * count of currently-active users reaches `minRequiredUsers`, we open
 * a common window; when it drops below, we close it.
 */
import moment from 'moment-timezone';
import type { InterviewKind, Interval } from './types';
import { mergeIntervals } from './mergeIntervals';

export interface ComputeCommonAvailabilityArgs {
  /** userId → their working-hours intervals for the window (ISO strings, any tz). */
  scheduleByUserId: Record<string, Interval[]>;
  timezone: string;
  interviewKind: InterviewKind;
  /** Ids that should not be counted as "required humans" in panel intersection (e.g. rooms). */
  nonHumanUserIds?: string[];
}

export function computeCommonAvailability({
  scheduleByUserId,
  timezone,
  interviewKind,
  nonHumanUserIds = [],
}: ComputeCommonAvailabilityArgs): Interval[] {
  const userIds = Object.keys(scheduleByUserId);
  if (userIds.length === 0) {
    return [];
  }

  if (interviewKind === 'one_on_one') {
    const everyInterval = userIds.flatMap((id) => scheduleByUserId[id] ?? []);
    return mergeIntervals(everyInterval, timezone);
  }

  // panel → intersection
  const humanIds = userIds.filter((id) => !nonHumanUserIds.includes(id));
  const minRequired = humanIds.length;
  if (minRequired < 2) {
    // Panel with <2 humans collapses to "whoever is left" — treat like union.
    const onlyHumans = humanIds.flatMap((id) => scheduleByUserId[id] ?? []);
    return mergeIntervals(onlyHumans, timezone);
  }

  return mergeIntervals(
    sweepIntersection(
      humanIds.map((id) => scheduleByUserId[id] ?? []),
      timezone,
      minRequired
    ),
    timezone
  );
}

function sweepIntersection(
  userIntervals: Interval[][],
  timezone: string,
  minRequired: number
): Interval[] {
  type Event = { at: moment.Moment; kind: 'start' | 'end'; who: number };
  const events: Event[] = [];

  userIntervals.forEach((ivs, who) => {
    ivs.forEach((iv) => {
      const start = moment.tz(iv.start, timezone);
      const end = moment.tz(iv.end, timezone);
      if (!start.isValid() || !end.isValid() || start.isSameOrAfter(end)) {
        return;
      }
      events.push({ at: start, kind: 'start', who });
      events.push({ at: end, kind: 'end', who });
    });
  });

  if (events.length === 0) {
    return [];
  }

  // Sort by time; when times tie, process ends before starts so adjacent
  // slots don't fake an "extra" overlap at the shared edge.
  events.sort((a, b) => {
    const diff = a.at.valueOf() - b.at.valueOf();
    if (diff !== 0) return diff;
    return a.kind === 'end' ? -1 : 1;
  });

  const result: Interval[] = [];
  const active = new Set<number>();
  let windowStart: moment.Moment | null = null;

  for (const evt of events) {
    if (evt.kind === 'start') {
      active.add(evt.who);
      if (active.size >= minRequired && windowStart === null) {
        windowStart = evt.at;
      }
    } else {
      if (active.size >= minRequired && windowStart !== null) {
        result.push({
          start: windowStart.toISOString(),
          end: evt.at.toISOString(),
        });
        windowStart = null;
      }
      active.delete(evt.who);
      if (active.size >= minRequired && windowStart === null) {
        windowStart = evt.at;
      }
    }
  }

  return result;
}
